// beingMade — what the running turn is making, in the Author's own words,
// for the place its result lands (principle 2: the first visible change
// follows at once, where the result will land). Pure, over the chat session.
//
// It claims only what is true: the Author asked (their latest message) and
// work is running (the turn is drafting). It ends when the turn ends, or as
// soon as the turn's first real change has landed: a course or parts planned,
// a material made. From then on the change itself is what shows. Nothing is
// guessed ahead of the real result: the only parts shown before it lands are
// the ones the assistant has already written into its plan call (plan_course
// or plan_parts; the outline draft), marked as being made, and they go when
// that call closes, landed or failed: on landing the real parts replace
// them, on failure nothing claims they exist.

export { beingMadeOf };

import { exhaustSwitchCase } from "@bilda/utils";
import type {
    ChatEntry,
    OutlineDraft,
    ToolCallDetail,
} from "../../components/Chat/types.js";
import type { ChatSession } from "./model.js";

type BeingMade = {
    readonly words: string;
    // The outline written so far, while its plan call is open.
    readonly outline: Omit<OutlineDraft, "toolCallId"> | undefined;
};

// A finished step that changed material the Author can now see.
const landed = (detail: ToolCallDetail | undefined): boolean => {
    switch (detail?.type) {
        case "parts_planned":
            return true;
        case "resource_touched":
            return detail.resource.kind === "material";
        case "citation_added":
        case "package_exported":
        case "resources_listed":
        case "source_still_processing":
        case "recorded_claim":
        case undefined:
            return false;
        default:
            return exhaustSwitchCase(detail);
    }
};

const landedIn = (entry: ChatEntry): boolean => {
    switch (entry.type) {
        case "material":
            return true;
        case "agent_activity":
            return entry.steps.some(
                (s) =>
                    s.type === "tool_done" &&
                    s.outcome.type === "completed" &&
                    landed(s.outcome.detail),
            );
        default:
            return false;
    }
};

// The call has closed: its step in the trail is done (completed or failed).
const closedIn =
    (toolCallId: string) =>
    (entry: ChatEntry): boolean =>
        entry.type === "agent_activity" &&
        entry.steps.some((s) => s.type === "tool_done" && s.id === toolCallId);

const outlineOf = (
    draft: OutlineDraft | null,
    entries: readonly ChatEntry[],
): BeingMade["outline"] =>
    draft === null || entries.some(closedIn(draft.toolCallId))
        ? undefined
        : {
              ...(draft.title === undefined ? {} : { title: draft.title }),
              parts: draft.parts,
          };

// O(n) in the transcript's length (one scan back to the latest message).
const beingMadeOf = (session: ChatSession): BeingMade | undefined => {
    if (!session.drafting) return undefined;
    const entries = session.conversation;
    const at = entries.reduce(
        (last, e, i) => (e.type === "author" ? i : last),
        -1,
    );
    const asked = entries[at];
    if (asked?.type !== "author") return undefined;
    const words = asked.text.trim();
    return words === "" || entries.slice(at + 1).some(landedIn)
        ? undefined
        : { words, outline: outlineOf(session.outlineDraft, entries) };
};
